// Read EVERY ticket behind a Dashboard view, not just the pages on screen.
//
// search_tickets_v2 returns at most 100 rows per call and clamps the offset to
// 5,000, so "all of them" is a short loop. Used by the FYI tab's "Clear all":
// the button must act on every robot-mail ticket, not on the 100 that happen to
// be loaded.
//
// A failed page rejects, so nobody ever gets a partial list that looks complete,
// and `complete` says honestly whether the offset limit cut the list short.
// Pure on purpose (the page fetcher is passed in): tests/search-ids.test.ts.

import type { TicketSearchPage } from './search-query';

/** The RPC clamps p_offset to this (supabase/migrations/076). */
export const SEARCH_OFFSET_MAX = 5000;

export interface AllSearchTickets<T> {
  tickets: T[];
  /** The server's total for the view (what the badge shows). */
  total: number;
  /** false when the offset limit (or an empty page) stopped the loop early. */
  complete: boolean;
}

export async function fetchAllSearchTickets<T extends { id: string }>(
  fetchPage: (offset: number) => Promise<TicketSearchPage<T>>,
  opts: { signal?: AbortSignal } = {},
): Promise<AllSearchTickets<T>> {
  const seen = new Set<string>();
  const tickets: T[] = [];
  let total = 0;
  let offset = 0;
  let complete = true;

  for (;;) {
    if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const page = await fetchPage(offset);
    total = page.total;
    for (const r of page.rows) {
      if (seen.has(r.ticket.id)) continue;
      seen.add(r.ticket.id);
      tickets.push(r.ticket);
    }
    // Done: nothing more to read (also the empty-list case).
    if (tickets.length >= total) break;
    // The server gave us nothing new for this offset although it says there is
    // more: stop rather than loop, and say the list may be incomplete.
    if (page.rows.length === 0) {
      complete = false;
      break;
    }
    offset += page.rows.length;
    if (offset > SEARCH_OFFSET_MAX) {
      complete = false;
      break;
    }
  }
  return { tickets, total, complete };
}
